import React, {FocusEvent, ChangeEvent, Fragment, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import cloneDeep from 'lodash/cloneDeep';
import b_ from 'b_';

import Block from 'client/common/components/block';
import {Tumbler} from 'client/common/components/lego';
import SizeBlock from 'client/common/components/size-block';
import AudienceBlock from 'client/common/components/audience-block';

import I18N from 'assets/i18n/brandlift-project';

import {CreativeBox, Survey} from 'client/common/types';

import {CreativeInfo} from 'client/creatives/utils/parse';

import CustomQuestions from './custom-questions';
import NameBlock from './name-block';
import VideoBlock from './video-block';
import SettingsBlock from './settings-block';
import BrandUniqueBlock from './brand-unique-block';
import BottomButtons from './bottom-buttons';
import ParametersBlock from './parameters-block';
import LanguageBlock from './language-block';

import {setImmut, wrapArray} from './utils';
import {CreativeFormProps, CreativeFormValues} from './types';

import './creative-settings-form.css';

const b = b_.with('creative-settings-form');

const i18n = I18N.creatives.creativeSettings;

interface UseFormProps {
    box: CreativeBox;
    name: Survey['name'];
    creative: CreativeInfo;
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
}

// eslint-disable-next-line max-statements
function useFormProps(props: UseFormProps) {
    const {box, name, updateSurveyBoxContext} = props;

    const [heroes, setHeroes] = useState(box?.context?.optionalQuestions?.heroes ?? false);
    const [story, setStory] = useState(box?.context?.optionalQuestions?.story ?? false);
    const [brandName, setBrandName] = useState(box?.context?.brand ?? '');
    const [isCustomQuestionsEnabled, setIsCustomQuestionsEnabled] = useState(
        box?.context?.customQuestions?.enabled ?? false
    );

    const handleBrandUpdate = async (e: FocusEvent<HTMLInputElement>) => {
        setBrandName(e.target.value);
        await updateSurveyBoxContext(setImmut(box, 'context.brand', e.target.value));
    };

    const handleHeroesUpdate = async (e: ChangeEvent<HTMLInputElement>) => {
        const {checked} = e.target;

        setHeroes(checked);
        await updateSurveyBoxContext(setImmut(box, 'context.optionalQuestions.heroes', checked));
    };

    const handleStoryUpdate = async (e: ChangeEvent<HTMLInputElement>) => {
        const {checked} = e.target;

        setStory(checked);
        await updateSurveyBoxContext(setImmut(box, 'context.optionalQuestions.story', checked));
    };

    const {reset} = useFormContext();

    const handleCustomQuestionUpdate = async (e: ChangeEvent<HTMLInputElement>) => {
        const {checked} = e.target;

        const data = cloneDeep(
            box?.context?.customQuestions ?? {
                questions: [
                    {
                        multipleChoice: false,
                        options: ['', ''],
                        label: ''
                    }
                ]
            }
        );

        data.enabled = checked;

        setIsCustomQuestionsEnabled(checked);
        const newBox = setImmut(box, 'context.customQuestions', data);

        // Подцепляем изменения контекста коробки в форме
        reset(CreativeForm.getDefaultValue(newBox as CreativeBox, name));
        await updateSurveyBoxContext(newBox);
    };

    return {
        heroes,
        story,
        brandName,
        isCustomQuestionsEnabled,
        handleBrandUpdate,
        handleHeroesUpdate,
        handleStoryUpdate,
        handleCustomQuestionUpdate
    };
}

function CreativeForm(props: CreativeFormProps) {
    const {
        state,
        slug,
        name,
        box,
        disabled = false,
        creative,
        updateSurveyBoxContext
    } = props;

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {register, control, errors, setValue, clearErrors} = useFormContext<CreativeFormValues>();

    const {
        heroes,
        story,
        brandName,
        isCustomQuestionsEnabled,
        handleBrandUpdate,
        handleHeroesUpdate,
        handleStoryUpdate,
        handleCustomQuestionUpdate
    } = useFormProps({
        box,
        name,
        creative,
        updateSurveyBoxContext
    });

    const commonBlockProps = {
        box,
        slug,
        disabled,
        creative,
        state,
        register,
        errors,
        control,
        updateSurveyBoxContext,
        setValue,
        clearErrors
    };

    return (
        <Fragment>
            <LanguageBlock />
            <NameBlock {...commonBlockProps} />
            <VideoBlock
                label={i18n.new.video.label}
                description={i18n.new.video.desc}
                {...commonBlockProps}
            />
            <SettingsBlock handleBrandUpdate={handleBrandUpdate} {...commonBlockProps} />
            <AudienceBlock />
            <SizeBlock description={i18n.sizeBlock.description} />
            <BrandUniqueBlock {...commonBlockProps} />
            <Block header={i18n.new.extra.label} description={i18n.new.extra.desc}>
                <Tumbler
                    className={b('extra-block-tumbler')}
                    size='m'
                    view='dl'
                    checked={heroes}
                    disabled={disabled}
                    onChange={handleHeroesUpdate}
                    labelAfter={i18n.new.question.heroes.label.replace('%s', brandName)}
                />
                <Tumbler
                    className={b('extra-block-tumbler')}
                    size='m'
                    view='dl'
                    checked={story}
                    disabled={disabled}
                    onChange={handleStoryUpdate}
                    labelAfter={i18n.new.question.story.label.replace('%s', brandName)}
                />
            </Block>
            <CustomQuestions
                {...commonBlockProps}
                maxQuestions={3}
                isCustomQuestionsEnabled={isCustomQuestionsEnabled}
                handleCustomQuestionUpdate={handleCustomQuestionUpdate}
            />
            <ParametersBlock box={box} />
            <BottomButtons {...props} />
        </Fragment>
    );
}

CreativeForm.getDefaultValue = (box: CreativeBox, name: string): CreativeFormValues => {
    const options = box?.context?.options;

    return {
        creativeName: name ?? '',
        brandName: box?.context?.brand ?? '',
        industry: box?.context?.industry,
        options: {
            about: wrapArray(options?.about ?? ['', '', '', '', '']),
            image: wrapArray(options?.image ?? ['', '', '', '', ''])
        },
        customQuestions:
            box?.context?.customQuestions?.questions?.map(
                ({label, multipleChoice, options: questionOptions}) => ({
                    label,
                    type: multipleChoice ? 'multi' : 'single',
                    options: wrapArray(questionOptions)
                })
            ) ?? []
    };
};

export default CreativeForm;
